// components
import { Dialog } from 'components/shared';
import EmployeeSelector from 'components/shared/EmployeeSelector';
import Box from '@mui/material/Box';

// hooks
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useGetUsersEmployees from 'components/flex-portal/FlexMatch/hooks/useGetUsersEmployees';

// utils
import { startOfDay } from 'date-fns';

// types
import type { Employee } from 'components/shared/EmployeeSelector';

type EmployeePickerDialogProps = {
  open: boolean;
  onClose: () => void;
  onSelect: (employee: Employee) => void;
};

/**
 * Lets the user pick an employee before registering an absence from the general
 * overview, using the same selector and employee list as the employee card.
 */
const EmployeePickerDialog = ({
  open,
  onClose,
  onSelect
}: EmployeePickerDialogProps) => {
  const { t } = useTranslation('common');
  // the date is part of the query key, so it has to stay stable between renders:
  // a fresh `new Date()` per render keeps invalidating the query and refetching
  const referenceDate = useMemo(() => startOfDay(new Date()), []);
  const { data: employees } = useGetUsersEmployees(
    { date: referenceDate, onlyFlex: false },
    { enabled: open }
  );

  const options: Employee[] = (employees || []).map((employee) => ({
    ...employee,
    id: employee.empId,
    employeeId: employee.empId,
    employeeName: employee.nameInformal,
    label: `${employee.nr} - ${
      employee.nameCompoundSurname?.replace(/\r?\n|\r/, '') ?? ''
    }`
  }));

  return (
    <Dialog
      open={open}
      title={t('searchEmployee')}
      handleClose={onClose}
      maxWidth="medium"
      fullWidth
    >
      <Box minHeight={140} pt={1}>
        <EmployeeSelector
          options={options}
          fullWidth
          variant="outlined"
          onSelected={(employee) => {
            if (employee) {
              onSelect(employee);
            }
          }}
        />
      </Box>
    </Dialog>
  );
};

export default EmployeePickerDialog;
